import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  PaginatedTable,
  PaginationProps,
} from '@cnx-saas/common-fe';
import { UserOutput } from '@cnx-saas/common';
import { useAuth } from '@cnx-saas/react-auth';
import { useSnackbar } from '@cnx-saas/snackbar';
import { useResendInvitationMutation } from '../../../api/tenant/mutations';
import { useDeleteInviteDialog } from '../hooks/useDeleteInviteDialog';
import { FormatTableDate } from './FormatTableDate';
import { OverflowMenuInVited } from './OverflowMenuInvited';

type TableContext = {
  readonly t: TFunction;
  onResendInvitation(user: UserOutput): void;
  onDelete(user: UserOutput): void;
};

type InvitedUserTableProps = {
  users: UserOutput[];
  pagination: PaginationProps;
  loading: boolean;
  refetch: () => void;
};
export const InvitedUserTable = ({
  users,
  pagination,
  loading,
  refetch,
}: InvitedUserTableProps) => {
  const { t } = useTranslation();

  const { user: authUser } = useAuth(true);

  const { addAlert } = useSnackbar();

  const [resendInvite] = useResendInvitationMutation();

  const { openDeleteInviteDialog } = useDeleteInviteDialog(refetch);

  const onResendInvitation = async (user: UserOutput) => {
    const response = await resendInvite({
      variables: {
        details: {
          userId: user.id,
        },
      },
    });
    await refetch();

    if (response.errors || response.data?.sendInvitation.message !== 'ok') {
      addAlert({
        type: 'error',
        children: response.data?.sendInvitation.message ?? 'Error occured',
      });
    } else {
      addAlert({
        type: 'success',
        children: t('userManagement.form.invite.success', {
          defaultValue: 'Invitation has been sent',
        }),
      });
    }
  };

  const onDelete = async (user: UserOutput) => {
    openDeleteInviteDialog(user.id);
  };

  const columns: ColumnDef<UserOutput, TableContext>[] = [
    {
      header: ({ t }) => t('userManagement.filter.email', 'Email'),
      render: (user) => user.email,
    },
    {
      header: ({ t }) => t('userManagement.filter.invitedOn', 'Invited On'),
      render: (user) => <FormatTableDate date={user.invitedOn} />,
    },
    {
      header: () => '',
      render: (user, { onResendInvitation, onDelete }) =>
        authUser.email === user.email ? (
          ''
        ) : (
          <OverflowMenuInVited
            user={user}
            onResendInvitation={onResendInvitation}
            onDelete={onDelete}
          />
        ),
      padding: 'inline',
      sx: { width: '48px' },
    },
  ];
  return (
    <PaginatedTable<UserOutput, never, TableContext>
      columns={columns}
      getRowKey={(row) => row.id}
      rows={users}
      context={{
        t,
        onResendInvitation: onResendInvitation,
        onDelete: onDelete,
      }}
      pagination={pagination}
      loading={loading}
      sx={{ gridArea: 'table' }}
    />
  );
};
